import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import uiDefaultConfig from '../config/uiDefaultConfig';
import useUIConfigStore from '../store/uiConfigStore';
import { Alert, Box } from '@mui/material';

const UIConfigContext = createContext({
  config: uiDefaultConfig,
  loading: false,
  error: null,
});

const buildTheme = (config) => {
  const palette = config.palette || {};
  return createTheme({
    palette: {
      primary: { main: palette.primary },
      secondary: { main: palette.secondary },
      success: { main: palette.success },
      info: { main: palette.info },
      warning: { main: palette.warning },
      error: { main: palette.error },
      background: { paper: palette.backgroundPaper, default: palette.backgroundDefault },
      text: {
        primary: palette.textPrimary,
        secondary: palette.textSecondary,
      },
      action: {
        hover: palette.actionHover,
        selected: palette.actionHover,
      },
      divider: palette.divider,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--color-primary': palette.primary,
            '--color-secondary': palette.secondary,
            '--color-background': palette.backgroundDefault,
            '--color-paper': palette.backgroundPaper,
            '--color-text-primary': palette.textPrimary,
            '--color-text-secondary': palette.textSecondary,
            '--color-border-subtle': palette.borderSubtle,
            '--color-divider': palette.divider,
            '--color-action-hover': palette.actionHover,
          },
          body: {
            backgroundColor: palette.backgroundDefault,
            color: palette.textPrimary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderColor: palette.borderSubtle,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderColor: palette.borderSubtle,
          },
        },
      },
    },
  });
};

export const UIConfigProvider = ({ children }) => {
  const {
    config,
    loading,
    error,
    initialized,
    fetchUIConfig,
  } = useUIConfigStore();

  useEffect(() => {
    fetchUIConfig();
  }, [fetchUIConfig]);

  const theme = useMemo(() => buildTheme(config), [config]);

  const value = useMemo(() => ({ config, loading, error }), [config, loading, error]);

  return (
    <UIConfigContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {loading && !initialized && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            Loading UI...
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning">{error}</Alert>
          </Box>
        )}
        {children}
      </ThemeProvider>
    </UIConfigContext.Provider>
  );
};

export const useUIConfig = () => useContext(UIConfigContext);
